import React, { useState } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { emailService } from '../../services/emailService';
import { usageTrackingService } from '../../services/usageTracking';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  backdrop-filter: blur(4px);
`;

const PopupContainer = styled.div`
  background-color: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.xxl};
  padding: ${theme.spacing.xxxl};
  border: 1px solid ${theme.colors.border};
  box-shadow: ${theme.shadows.lg};
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  font-family: ${theme.typography.fontFamily};
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${theme.spacing.md};
  right: ${theme.spacing.md};
  background: none;
  border: none;
  color: ${theme.colors.text};
  font-size: ${theme.typography.fontSizes.xxl};
  cursor: pointer;
  padding: ${theme.spacing.xs};
  line-height: 1;
  transition: color ${theme.transitions.normal};
  
  &:hover {
    color: ${theme.colors.primary};
  }
`;

const Title = styled.h2`
  margin: 0 0 ${theme.spacing.xl} 0;
  color: ${theme.colors.text};
  text-align: center;
  font-size: ${theme.typography.fontSizes.xxxl};
  font-weight: ${theme.typography.fontWeights.bold};
  font-family: ${theme.typography.fontFamily};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const Label = styled.label`
  display: block;
  color: ${theme.colors.text};
  font-size: ${theme.typography.fontSizes.base};
  font-weight: ${theme.typography.fontWeights.medium};
  font-family: ${theme.typography.fontFamily};
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  background-color: ${theme.colors.background};
  color: ${theme.colors.text};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSizes.base};
  resize: vertical;
  min-height: 100px;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primaryHover};
  }
`;

const SuccessMessage = styled.div`
  color: ${theme.colors.success || '#50fa7b'};
  font-size: ${theme.typography.fontSizes.base};
  text-align: center;
  padding: ${theme.spacing.md};
  background-color: rgba(80, 250, 123, 0.1);
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.success || '#50fa7b'};
  font-family: ${theme.typography.fontFamily};
  font-weight: ${theme.typography.fontWeights.medium};
`;

const ErrorMessage = styled.div`
  color: ${theme.colors.error};
  font-size: ${theme.typography.fontSizes.base};
  text-align: center;
  padding: ${theme.spacing.sm};
  background-color: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.sm};
  border: 1px solid ${theme.colors.error};
  font-family: ${theme.typography.fontFamily};
  font-weight: ${theme.typography.fontWeights.normal};
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.md};
`;

const FeedbackPopup = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setSuccess(false);

    try {
      // Send email using email service API
      await emailService.sendFeedbackEmail(
        email,
        title,
        message
      );

      // Success - show message and reset form
      setSuccess(true);
      
      // Track feedback submission
      usageTrackingService.trackFeedbackSubmission(title);
      
      setEmail('');
      setTitle('');
      setMessage('');
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Email service error:', err);
      
      // Provide user-friendly error messages
      let errorMessage = 'Failed to submit feedback. Please try again.';
      
      if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setEmail('');
      setTitle('');
      setMessage('');
      setError('');
      setSuccess(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <Overlay onClick={handleClose}>
      <PopupContainer onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={handleClose} disabled={isSubmitting}>
          ×
        </CloseButton>
        <Title>Send Feedback</Title>
        {success ? (
          <SuccessMessage>
            ✓ Thank you! Your feedback has been submitted successfully.
          </SuccessMessage>
        ) : (
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="feedback-email">Email *</Label>
              <Input
                id="feedback-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                disabled={isSubmitting}
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="feedback-title">Title *</Label>
              <Input
                id="feedback-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter feedback title"
                required
                disabled={isSubmitting}
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="feedback-message">Message *</Label>
              <TextArea
                id="feedback-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Enter your feedback"
                required
                disabled={isSubmitting}
              />
            </FormGroup>
            {error && <ErrorMessage>{error}</ErrorMessage>}
            <ButtonContainer>
              <Button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                variant="ghost"
                style={{ flex: 1 }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || !email || !title || !message}
                style={{ flex: 1 }}
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </Button>
            </ButtonContainer>
          </Form>
        )}
      </PopupContainer>
    </Overlay>
  );
};

export default FeedbackPopup;

